import React, { useState, useEffect, useRef } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Button,
  Paper,
  Tooltip,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useAuth } from '../../contexts/AuthContext';
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead } from '../../services/notificationsService';
import { supabase } from '../../config/supabase';
import socketClient from '../../services/socketClient';
import NotificationItem from './NotificationItem';
import SkeletonLoader from '../shared/SkeletonLoader';
import EmptyState from '../shared/EmptyState';
import ErrorMessage from '../shared/ErrorMessage';
import IncomingCallModal from '../video/IncomingCallModal';

/**
 * NotificationBell Component
 * Displays notification bell icon with unread count and dropdown menu
 */
const NotificationBell = () => {
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const hasInitialized = useRef(false);

  const open = Boolean(anchorEl);

  // Fetch notifications and unread count
  const fetchNotifications = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const [notificationsResult, countResult] = await Promise.all([
        getNotifications({ limit: 20 }),
        getUnreadCount()
      ]);

      if (notificationsResult.data) {
        setNotifications(notificationsResult.data);
        
        // Check for unhandled call notifications in fetched data
        // This handles cases where Socket.io didn't trigger the modal
        const callNotifications = notificationsResult.data.filter(n => 
          isCallNotification(n) && !n.is_read
        );
        
        if (callNotifications.length > 0 && !incomingCall) {
          // Get the most recent call notification
          const latestCall = callNotifications[0];
          const metadata = latestCall.metadata || {};
          const callerName = latestCall.message?.split(' is calling')[0] || 
                            latestCall.message?.split(' calling')[0] || 
                            'Someone';
          
          // CRITICAL: Use actionUrl if available (contains full URL with params)
          let callUrl = metadata.actionUrl;
          
          if (!callUrl) {
            // Prefer roomName (actual Daily.co room name with apt- prefix) over appointmentId
            // This ensures both parties join the exact same room
            const roomIdentifier = metadata.roomName || metadata.appointmentId || metadata.roomId;
            
            if (roomIdentifier) {
              const params = new URLSearchParams({
                // Use roomName as appointmentId if available - backend will recognize it
                appointmentId: metadata.roomName || metadata.appointmentId || roomIdentifier,
                ...(metadata.participants && { participants: metadata.participants }),
                ...(metadata.callType && { type: metadata.callType }),
              });
              callUrl = `/video-call?${params.toString()}`;
              if (import.meta.env.DEV) console.log('[NotificationBell] Built call URL with identifier:', roomIdentifier);
            } else {
              console.error('[NotificationBell] No room identifier found in notification metadata:', metadata);
            }
          }

          if (!callUrl) {
            console.error('[NotificationBell] Cannot construct call URL - missing metadata');
            // Don't set incomingCall if we can't construct a valid URL
            return;
          }
          
          setIncomingCall({
            callerName: callerName.trim(),
            callType: metadata.callType || (latestCall.title?.toLowerCase().includes('video') ? 'video' : 'audio'),
            callUrl: callUrl || '/video-call',
            notificationId: latestCall.id,
          });
        }
      }

      if (countResult.data !== undefined) {
        setUnreadCount(countResult.data);
      }
    } catch (err) {
      // Handle different error types
      const errorMessage = err?.message || err?.toString() || '';
      const isConnectionError = err?.isConnectionError ||
                               errorMessage.includes('Backend server is not available') ||
                               errorMessage.includes('Failed to fetch') ||
                               errorMessage.includes('ERR_CONNECTION_REFUSED');
      const isRateLimitError = errorMessage.includes('Too many requests') ||
                               errorMessage.includes('429') ||
                               err?.status === 429;
      
      if (isRateLimitError) {
        // Handle rate limit gracefully - don't show error, just reduce polling
        console.warn('Rate limit reached for notifications - reducing polling frequency');
        // Don't set error state - user can still see cached notifications
        // Socket.io will still work for real-time updates
      } else if (!isConnectionError && import.meta.env.DEV) {
        console.error('Error fetching notifications:', err);
        setError('Failed to load notifications');
      }
      // Silently handle connection errors - Socket.io will still work
    } finally {
      setLoading(false);
    }
  };

  // Clear notification cache on version change (fixes hard refresh issue)
  useEffect(() => {
    const CACHE_VERSION = 'v2.1.0'; // Update when notification logic changes
    const cachedVersion = localStorage.getItem('notification-cache-version');
    
    if (cachedVersion !== CACHE_VERSION) {
      // Clear old cached data
      localStorage.removeItem('notifications');
      localStorage.removeItem('last-notification-fetch');
      localStorage.setItem('notification-cache-version', CACHE_VERSION);
      if (import.meta.env.DEV) console.log('[NotificationBell] Cache cleared - version updated to', CACHE_VERSION);
    }
  }, []);

  // Initial fetch and polling fallback
  useEffect(() => {
    if (user && !hasInitialized.current) {
      hasInitialized.current = true;
      fetchNotifications();

      // Subscribe to notifications via Socket.io
      socketClient.connect();
      socketClient.on('notification:new', handleNewNotification);
      
      // Polling fallback: Check for new notifications every 30 seconds (reduced frequency)
      // This ensures we catch notifications even if Socket.io is down
      // Reduced from 10s to 30s to avoid rate limiting issues
      const pollInterval = setInterval(() => {
        if (!incomingCall) {
          // Only poll if we don't have an active incoming call
          fetchNotifications();
        }
      }, 30000); // Poll every 30 seconds (was 10 seconds)
      
      return () => {
        if (hasInitialized.current) {
          socketClient.off('notification:new', handleNewNotification);
          clearInterval(pollInterval);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Check if notification is a call notification
  const isCallNotification = (notification) => {
    const title = (notification.title || '').toLowerCase();
    const message = (notification.message || '').toLowerCase();
    const metadata = notification.metadata || {};
    
    // Check title for call-related keywords
    const hasCallKeywords = title.includes('incoming') && 
                           (title.includes('call') || title.includes('video'));
    
    // Check message for call-related keywords
    const messageHasCall = message.includes('calling') || message.includes('call');
    
    // Check metadata for call indicators
    const hasCallMetadata = metadata.actionUrl?.includes('/video-call') ||
                           metadata.roomId ||
                           metadata.appointmentId ||
                           metadata.callType;
    
    return hasCallKeywords || (messageHasCall && hasCallMetadata);
  };

  // Handle new notification from Socket.io
  const handleNewNotification = (notification) => {
    // Check if this is a call notification
    if (isCallNotification(notification)) {
      const metadata = notification.metadata || {};
      const callerName = notification.message?.split(' is calling')[0] || 
                        notification.message?.split(' calling')[0] || 
                        'Someone';
      
      // Construct call URL - CRITICAL: Use roomName if available (has apt- prefix)
      // This ensures both parties join the exact same room
      let callUrl = metadata.actionUrl;
      if (!callUrl) {
        // Prefer roomName (actual Daily.co room name with apt- prefix) over appointmentId
        // The backend will use this to find the existing room
        const roomIdentifier = metadata.roomName || metadata.roomId || metadata.appointmentId;
        
        if (roomIdentifier) {
          const params = new URLSearchParams({
            // Use roomName as appointmentId if available - backend will recognize it
            appointmentId: metadata.roomName || metadata.appointmentId || roomIdentifier,
            ...(metadata.participants && { participants: metadata.participants }),
            ...(metadata.callType && { type: metadata.callType }),
          });
          callUrl = `/video-call?${params.toString()}`;
          if (import.meta.env.DEV) console.log('[NotificationBell] Built call URL with identifier:', roomIdentifier);
        }
      }

      setIncomingCall({
        callerName: callerName.trim(),
        callType: metadata.callType || (notification.title?.toLowerCase().includes('video') ? 'video' : 'audio'),
        callUrl: callUrl || '/video-call',
        notificationId: notification.id,
      });
    }

    // Add to top of list
    setNotifications(prev => [notification, ...prev].slice(0, 20));
    // Increment unread count
    setUnreadCount(prev => prev + 1);
  };

  // Handle click on bell icon
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Refresh notifications when opening
    if (open === false) {
      fetchNotifications();
    }
  };

  // Handle close
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle mark as read
  const handleMarkAsRead = async (notificationId) => {
    // Check if user is still authenticated before marking as read
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.warn('Cannot mark notification as read - user not authenticated');
      return;
    }
    
    try {
      const result = await markAsRead(notificationId);
      if (!result.error) {
        // Update local state
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n)
        );
        // Decrement unread count
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      // If marking as read fails due to auth, don't block the call
      console.warn('Failed to mark notification as read:', err);
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    const result = await markAllAsRead();
    if (!result.error) {
      // Update all notifications
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    }
  };

  // Format notification time
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Handle incoming call accept
  const handleAcceptCall = async () => {
    // Mark as read only if we have a valid notification ID
    // Don't block on this - navigation is more important
    if (incomingCall?.notificationId) {
      // Mark as read in background - don't await
      handleMarkAsRead(incomingCall.notificationId).catch(err => {
        console.warn('Failed to mark notification as read:', err);
        // Don't block navigation on this error
      });
    }
    setIncomingCall(null);
  };

  // Handle incoming call decline
  const handleDeclineCall = () => {
    if (incomingCall?.notificationId) {
      handleMarkAsRead(incomingCall.notificationId);
    }
    setIncomingCall(null);
  };

  return (
    <>
      {/* Incoming Call Modal */}
      {incomingCall && (
        <IncomingCallModal
          open={!!incomingCall}
          callerName={incomingCall.callerName}
          callType={incomingCall.callType}
          callUrl={incomingCall.callUrl}
          onAccept={handleAcceptCall}
          onDecline={handleDeclineCall}
          onClose={() => setIncomingCall(null)}
        />
      )}

      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          onClick={handleClick}
          sx={{
            transition: 'transform 0.2s ease',
            '&:hover': {
              transform: 'scale(1.1)',
            }
          }}
        >
          <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={99}>
            {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 360,
            maxWidth: 420,
            maxHeight: 600,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            borderRadius: 2,
          }
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                onClick={handleMarkAllAsRead}
                sx={{ textTransform: 'none' }}
              >
                Mark all read
              </Button>
            )}
          </Box>
        </Box>

        <Divider />

        <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
          {loading && notifications.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <SkeletonLoader variant="list" count={3} height={60} />
            </Box>
          ) : error ? (
            <ErrorMessage
              title="Unable to load notifications"
              message={error}
              actionLabel="Retry"
              onAction={fetchNotifications}
              severity="error"
              sx={{ p: 2 }}
            />
          ) : notifications.length === 0 ? (
            <EmptyState
              title="No notifications"
              description="You're all caught up! New notifications will appear here."
              icon={<NotificationsNoneIcon />}
              variant="minimal"
            />
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id || index}>
                  <NotificationItem
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    formatTime={formatTime}
                    onCallNotificationClick={(notif) => {
                      // Handle call notification click - show IncomingCallModal
                      if (isCallNotification(notif)) {
                        const metadata = notif.metadata || {};
                        const callerName = notif.message?.split(' is calling')[0] || 
                                          notif.message?.split(' calling')[0] || 
                                          'Someone';
                        
                        // Debug logging
                        if (import.meta.env.DEV) console.log('[NotificationBell] Call notification clicked:', {
                          notificationId: notif.id,
                          metadata,
                          hasActionUrl: !!metadata.actionUrl,
                          hasRoomName: !!metadata.roomName,
                          hasAppointmentId: !!metadata.appointmentId,
                          hasRoomId: !!metadata.roomId,
                        });
                        
                        const callUrl = (() => {
                          // CRITICAL: Use actionUrl if available (contains full URL with params)
                          let url = metadata.actionUrl;
                          
                          if (!url) {
                            // Prefer roomName (actual Daily.co room name with apt- prefix) over appointmentId
                            // This ensures both parties join the exact same room
                            const roomIdentifier = metadata.roomName || metadata.appointmentId || metadata.roomId;
                            
                            if (roomIdentifier) {
                              const params = new URLSearchParams({
                                // Use roomName as appointmentId if available - backend will recognize it
                                appointmentId: metadata.roomName || metadata.appointmentId || roomIdentifier,
                                ...(metadata.participants && { participants: metadata.participants }),
                                ...(metadata.callType && { type: metadata.callType }),
                              });
                              url = `/video-call?${params.toString()}`;
                              if (import.meta.env.DEV) console.log('[NotificationBell] Built call URL with identifier:', roomIdentifier);
                            } else {
                              console.error('[NotificationBell] No room identifier found in notification metadata:', metadata);
                            }
                          } else {
                            if (import.meta.env.DEV) console.log('[NotificationBell] Using actionUrl from metadata:', url);
                          }
                          
                          if (!url || url === '/video-call') {
                            console.error('[NotificationBell] Cannot construct valid call URL - missing metadata');
                            console.error('[NotificationBell] Full notification:', notif);
                            // Return empty string to show error in IncomingCallModal
                            return '';
                          }
                          
                          return url;
                        })();
                        
                        if (!callUrl) {
                          alert('Cannot join call: Missing call information. Please ask the caller to start a new call.');
                          return;
                        }
                        
                        setIncomingCall({
                          callerName: callerName.trim(),
                          callType: metadata.callType || (notif.title?.toLowerCase().includes('video') ? 'video' : 'audio'),
                          callUrl,
                          notificationId: notif.id,
                        });
                      }
                    }}
                  />
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;

